import React, { useState, useEffect, useMemo } from 'react';
import { db, auth } from './firebase';
import { collection, getDocs, doc, addDoc, serverTimestamp, increment, runTransaction } from 'firebase/firestore';
import { BUSINESS_TYPES, getBusinessType } from './businessTypes';

const formatTk = (num) => new Intl.NumberFormat('en-IN').format(Math.round(num || 0));

// একই প্রোডাক্ট আগের চেয়ে ভিন্ন দামে আবার কিনলে, আগের স্টক আর নতুন স্টক মিলিয়ে
// একটা গড় (weighted average) কেনা দাম বের করে -- যাতে পুরনো স্টকের আসল দামটা
// হারিয়ে না যায় (নতুন Purchase দিলে যেটা সরাসরি ওভাররাইট হয়ে যেত)।
const AverageCostPurchase = () => {
  const [products, setProducts] = useState([]);
  const [businessType, setBusinessType] = useState(BUSINESS_TYPES[0]);
  const [productId, setProductId] = useState('');
  const [newQty, setNewQty] = useState('');
  const [newUnitCost, setNewUnitCost] = useState('');
  const [otherCost, setOtherCost] = useState('');
  const [newSellingPrice, setNewSellingPrice] = useState('');
  const [supplierName, setSupplierName] = useState('');
  const [supplierPhone, setSupplierPhone] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getDocs(collection(db, 'products')).then((snap) => {
      setProducts(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
  }, []);

  const productsOfType = useMemo(
    () => products.filter((p) => getBusinessType(p) === businessType),
    [products, businessType]
  );

  const selectedProduct = products.find((p) => p.id === productId) || null;

  useEffect(() => {
    if (selectedProduct) setNewSellingPrice(String(selectedProduct.price || ''));
  }, [selectedProduct]);

  const qtyNum = Math.max(0, parseFloat(newQty) || 0);
  const unitCostNum = Math.max(0, parseFloat(newUnitCost) || 0);
  const otherCostNum = Math.max(0, parseFloat(otherCost) || 0);
  const perUnitExtra = qtyNum > 0 ? Math.round(otherCostNum / qtyNum) : 0;
  const newBatchUnitCost = unitCostNum + perUnitExtra;
  const newBatchTotalCost = (unitCostNum * qtyNum) + otherCostNum;

  const existingStock = selectedProduct?.stock || 0;
  const existingCost = selectedProduct?.costPrice || 0;
  const combinedStock = existingStock + qtyNum;
  const blendedCost = combinedStock > 0
    ? Math.round((existingStock * existingCost + qtyNum * newBatchUnitCost) / combinedStock)
    : 0;

  const canPreview = selectedProduct && qtyNum > 0 && unitCostNum > 0;

  const reset = () => {
    setProductId(''); setNewQty(''); setNewUnitCost(''); setOtherCost('');
    setNewSellingPrice(''); setSupplierName(''); setSupplierPhone('');
  };

  const submit = async () => {
    if (!selectedProduct) return alert('Please select a product first!');
    if (qtyNum <= 0 || unitCostNum <= 0) return alert('Please enter a valid quantity and unit cost!');

    setSaving(true);
    try {
      const currentUser = auth.currentUser;
      const addedByName = currentUser?.displayName || currentUser?.email?.split('@')[0] || 'Sohan';
      const localISOTime = new Date(new Date().getTime() - (new Date().getTimezoneOffset() * 60000)).toISOString();

      const historyEntry = {
        date: localISOTime,
        costPrice: newBatchUnitCost, // এই ব্যাচের আসল দাম -- গড় নয়
        baseRate: unitCostNum,
        otherCost: otherCostNum,
        sellingPrice: Number(newSellingPrice) || selectedProduct.price || 0,
        quantity: qtyNum,
        totalCost: newBatchTotalCost,
        addedBy: addedByName,
        supplierName: supplierName.trim() || 'General Supplier',
        supplierPhone: supplierPhone.trim() || 'N/A',
        note: 'Average Cost Purchase'
      };

      // ---- আগে এখানে selectedProduct.history/stock/costPrice ব্যবহার করা হতো, যেগুলো
      // পেজ লোড হওয়ার সময় একবার আনা হয়েছিল -- ততক্ষণে অন্য কোনো Purchase একই প্রোডাক্টে
      // স্টক/costPrice/history আপডেট করে থাকলে সেটা এখানে ধরা পড়তো না, আর সেই আপডেটটা
      // হারিয়ে যেত। এখন runTransaction দিয়ে সেভ করার ঠিক আগমুহূর্তে সার্ভার থেকে সর্বশেষ
      // ডেটা পড়ে তার উপর ভিত্তি করে গড় দাম ও history হিসাব করা হচ্ছে। ----
      let finalBlendedCost = blendedCost;
      let finalCombinedStock = combinedStock;
      await runTransaction(db, async (transaction) => {
        const productRef = doc(db, 'products', selectedProduct.id);
        const freshSnap = await transaction.get(productRef);
        const freshData = freshSnap.exists() ? freshSnap.data() : selectedProduct;
        const freshStock = Number(freshData.stock || 0);
        const freshCost = Number(freshData.costPrice || 0);
        const freshCombinedStock = freshStock + qtyNum;
        const freshBlendedCost = freshCombinedStock > 0
          ? Math.round((freshStock * freshCost + qtyNum * newBatchUnitCost) / freshCombinedStock)
          : 0;
        finalBlendedCost = freshBlendedCost;
        finalCombinedStock = freshCombinedStock;
        transaction.update(productRef, {
          stock: increment(qtyNum),
          costPrice: freshBlendedCost, // ভবিষ্যতের প্রফিট হিসাবের জন্য গড় দাম
          price: Number(newSellingPrice) || selectedProduct.price || 0,
          history: [...(freshData.history || []), historyEntry]
        });
      });

      // Purchase History/Expense রিপোর্টে আসল খরচটাই (গড় নয়) রেকর্ড হবে
      await addDoc(collection(db, 'purchases'), {
        // Date.now() (base36) -- Sales.js-এর invoiceId-এর মতোই -- যাতে দুইটা আলাদা
        // Purchase ভাউচার কখনো একই রেফারেন্স নম্বর শেয়ার না করে (আগে প্লেইন ৬-ডিজিট
        // র‍্যান্ডম নম্বর দিয়ে সেই সম্ভাবনা ছিল)।
        purchaseId: 'PUR-' + Date.now().toString(36).toUpperCase(),
        productName: selectedProduct.name,
        businessType,
        category: selectedProduct.category || '',
        costPrice: newBatchUnitCost,
        baseRate: unitCostNum,
        otherCost: otherCostNum,
        sellingPrice: Number(newSellingPrice) || selectedProduct.price || 0,
        quantity: qtyNum,
        totalCost: newBatchTotalCost,
        supplierName: supplierName.trim() || 'General Supplier',
        supplierPhone: supplierPhone.trim() || 'N/A',
        addedBy: addedByName,
        date: localISOTime,
        createdAt: serverTimestamp()
      });

      // লোকাল লিস্টও আপডেট করা হচ্ছে যাতে পরের হিসাব সাথে সাথে ঠিক দেখায়
      setProducts((prev) => prev.map((p) => p.id === selectedProduct.id
        ? { ...p, stock: finalCombinedStock, costPrice: finalBlendedCost, price: Number(newSellingPrice) || p.price }
        : p));

      alert(`✅ Updated! New stock: ${finalCombinedStock} pcs, average cost: ৳${formatTk(finalBlendedCost)}`);
      reset();
    } catch (err) {
      alert('Error: ' + err.message);
    }
    setSaving(false);
  };

  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
      <div className="white-box" style={{ width: '100%', maxWidth: '520px' }}>
        <h2 style={{ textAlign: 'center', color: '#4a00e0', marginBottom: '4px' }}>Average Cost Purchase</h2>
        <p style={{ textAlign: 'center', color: '#888', fontSize: '12px', marginBottom: '20px' }}>
          For buying more of a product you already have, at a different price — blends the old and new stock into one average cost.
        </p>

        <label style={{ fontSize: '12px', color: '#888', display: 'block', marginBottom: '4px' }}>Main Category</label>
        <select value={businessType} onChange={(e) => { setBusinessType(e.target.value); setProductId(''); }} className="input-field">
          {BUSINESS_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>

        <label style={{ fontSize: '12px', color: '#888', display: 'block', marginBottom: '4px' }}>Existing Product</label>
        <select value={productId} onChange={(e) => setProductId(e.target.value)} className="input-field">
          <option value="">-- Select Product --</option>
          {productsOfType.map((p) => <option key={p.id} value={p.id}>{p.name} (Stock: {p.stock || 0}, Cost: ৳{formatTk(p.costPrice)})</option>)}
        </select>

        {selectedProduct && (
          <div style={{ background: '#f4f7fe', padding: '10px 15px', borderRadius: '10px', fontSize: '12px', marginBottom: '15px' }}>
            Currently have: <b>{existingStock} pcs</b> @ <b>৳{formatTk(existingCost)}</b> each
            (worth ৳{formatTk(existingStock * existingCost)})
          </div>
        )}

        <div style={{ display: 'flex', gap: '10px' }}>
          <input type="number" min="0" value={newQty} onChange={(e) => setNewQty(e.target.value)} placeholder="New Quantity (Pcs)" className="input-field" />
          <input type="number" min="0" value={newUnitCost} onChange={(e) => setNewUnitCost(e.target.value)} placeholder="New Unit Cost (৳)" className="input-field" />
        </div>

        <input type="number" min="0" value={otherCost} onChange={(e) => setOtherCost(e.target.value)} placeholder="Conveyance / Transport (৳, optional)" className="input-field" />

        <label style={{ fontSize: '12px', color: '#888', display: 'block', marginBottom: '4px' }}>New Selling Price</label>
        <input type="number" min="0" value={newSellingPrice} onChange={(e) => setNewSellingPrice(e.target.value)} placeholder="৳" className="input-field" />

        <div style={{ display: 'flex', gap: '10px' }}>
          <input value={supplierName} onChange={(e) => setSupplierName(e.target.value)} placeholder="Supplier Name (optional)" className="input-field" />
          <input value={supplierPhone} onChange={(e) => setSupplierPhone(e.target.value)} placeholder="Phone Number (optional)" className="input-field" />
        </div>

        {canPreview && (
          <div style={{ background: '#fff8ec', border: '1px solid #f0d9a8', padding: '12px 15px', borderRadius: '10px', fontSize: '13px', margin: '10px 0 15px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ color: '#888', fontSize: '11px' }}>
                  <th align="left"></th>
                  <th align="right">Pcs</th>
                  <th align="right">Unit Cost</th>
                  <th align="right">Subtotal</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Existing</td>
                  <td align="right">{existingStock}</td>
                  <td align="right">৳{formatTk(existingCost)}</td>
                  <td align="right">৳{formatTk(existingStock * existingCost)}</td>
                </tr>
                <tr>
                  <td>New Purchase</td>
                  <td align="right">{qtyNum}</td>
                  <td align="right">৳{formatTk(newBatchUnitCost)}</td>
                  <td align="right">৳{formatTk(newBatchTotalCost)}</td>
                </tr>
                <tr style={{ borderTop: '1px solid #f0d9a8', fontWeight: 'bold', color: '#b7791f' }}>
                  <td>Combined</td>
                  <td align="right">{combinedStock}</td>
                  <td align="right">৳{formatTk(blendedCost)}</td>
                  <td align="right">৳{formatTk(existingStock * existingCost + newBatchTotalCost)}</td>
                </tr>
              </tbody>
            </table>
            <p style={{ margin: '10px 0 0', color: '#b7791f' }}>
              New Average Cost Price will be set to <b>৳{formatTk(blendedCost)}</b> per piece.
            </p>
          </div>
        )}

        <button onClick={submit} disabled={saving || !canPreview} className="btn-confirm" style={{ width: '220px', display: 'block', margin: '10px auto 0', opacity: (saving || !canPreview) ? 0.6 : 1 }}>
          {saving ? 'Saving...' : 'Update Stock & Average Cost'}
        </button>
      </div>
    </div>
  );
};

export default AverageCostPurchase;
